/** RFC 0345 3a: can this window make the recovery passkey account setup needs?
 *
 * Asked BEFORE setup creates a World, so a window that cannot make one says so
 * plainly and offers the other path instead of leaving a half-made account
 * behind a raw WebAuthn error.
 *
 * The desktop app never makes a passkey: first-device setup happens in the
 * system browser, and the desktop window joins by device approval.
 */

export type PasskeySupport =
  | { ok: true }
  | {
      ok: false
      reason: 'no-webauthn' | 'no-prf' | 'desktop'
      message: string
    }

interface PasskeyHost {
  userAgent: string
  PublicKeyCredential?: {
    getClientCapabilities?: () => Promise<Record<string, boolean | undefined>>
  }
}

/** The Mod desktop shell is Electron; a browser tab is not. */
export const isDesktopWindow = (userAgent: string) =>
  /\bElectron\//.test(userAgent)

export const DESKTOP_PASSKEY_MESSAGE =
  'Set up your account in your browser, then come back and choose Join this device.'

function hostOf(): PasskeyHost {
  const g = globalThis as {
    navigator?: { userAgent?: string }
    PublicKeyCredential?: PasskeyHost['PublicKeyCredential']
  }
  return {
    userAgent: g.navigator?.userAgent ?? '',
    PublicKeyCredential: g.PublicKeyCredential,
  }
}

export async function passkeySupport(
  host: PasskeyHost = hostOf(),
): Promise<PasskeySupport> {
  if (isDesktopWindow(host.userAgent))
    return { ok: false, reason: 'desktop', message: DESKTOP_PASSKEY_MESSAGE }
  const credential = host.PublicKeyCredential
  if (!credential)
    return {
      ok: false,
      reason: 'no-webauthn',
      message:
        'This browser cannot make passkeys. Open Mod in a current Chrome, Safari or Firefox to set up your account.',
    }
  // Only a definite "no" refuses: an older browser without the capability
  // query may still return PRF, and createPasskeyRecovery checks the result.
  const capabilities = await credential
    .getClientCapabilities?.()
    .catch(() => undefined)
  if (capabilities?.['extension:prf'] === false)
    return {
      ok: false,
      reason: 'no-prf',
      message:
        'This browser cannot make the kind of passkey that protects your recovery backup. Use a current Chrome or Safari to set up your account.',
    }
  return { ok: true }
}

/** A setup failure in words a person can act on, not a DOMException name. */
export function passkeySetupError(error: unknown): string {
  const name = (error as { name?: unknown } | null)?.name
  const message = error instanceof Error ? error.message : ''
  if (name === 'NotAllowedError' || name === 'AbortError')
    return 'The passkey was cancelled or timed out. Try again when you are ready.'
  if (name === 'InvalidStateError')
    return 'A Mod passkey already exists on this device for this account. Choose it, or remove it in your passwords settings and try again.'
  if (name === 'NotSupportedError' || name === 'SecurityError')
    return 'This browser refused to make a passkey for this site. Try a current Chrome or Safari.'
  if (
    /does not support encrypted recovery|cannot unlock encrypted recovery/.test(
      message,
    )
  )
    return 'That passkey cannot protect a recovery backup. Try again and save the passkey to iCloud Keychain or Google Password Manager.'
  if (name === 'TypeError' && /fetch|network|load failed/i.test(message))
    return 'Could not reach the Mod account service. Check your connection and try again.'
  return message || 'Account setup failed'
}
